import config from '@/config'

/**
 * Returns the customer number as regular number (eg 10000 instead of 10.000)
 * @param {array} possibleValues - an array of possible column values
 * @returns {string} customerNumber
 */
export const sanitizeCustomerNumberForTableView = (possibleValues) => {
  const customerNumber = possibleValues.filter(value => typeof value !== 'undefined').reduce((result, current) => {
    if (typeof (current) === 'number') {
      result = current
    } else if (typeof (current) === 'string') {
      result = `${parseInt(current.replace(/\s*.%/g, ''), 10) || ''}`
    }
    return result
  }, '')

  return customerNumber
}

/**
 * Returns the customer kind as string : 'privat' or 'firma'
 * @param {object} possibleValues - holding possible values
 * @param {string} importFromWindows - determines wether an import is from OM Win
 * @returns {string} customerKind
 */
export const defineCustomerKind = (possibleValues, importFromWindows) => {
  const customerKindPrivatAsBool = 1
  let customerKind = 'firma'

  if (importFromWindows) {
    customerKind = possibleValues.vorname !== '' || (typeof possibleValues.kundenart === 'number' && possibleValues.kundenart === customerKindPrivatAsBool) ? 'privat' : 'firma'
  } else {
    customerKind = possibleValues.art.toLowerCase()
  }

  return customerKind
}

/**
 * Returns a discount from 0 to max, as string
 * @param {array} possibleValues - an array of possible column values
 * @param {string} max - maximum discount value
 * @returns {string} discount
 */
export const sanitizeDiscountForTableView = (max, possibleValues) => {
  const { defaultCountryAsISO2 } = config
  const maxAsFloat = parseFloat(max)

  return possibleValues.filter(value => value !== undefined).map((value) => {
    if (typeof value === 'string') {
      value !== '' ? value = parseInt(value.replace(/\s*.%/g, '')) : value = 0
    }
    if (typeof value === 'number' && value < 1) {
      value = value * 100
    }

    if (value > maxAsFloat) {
      value = maxAsFloat
    }

    return value.toLocaleString(defaultCountryAsISO2, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %'
  }).reduce((result, item) => {
    if (item !== '') { result = item; return item }
  }, '0,00 %')
}

/**
 * Cast discount values as string to number
 * @param {string} value
 * @returns {number}
 */
export const castDiscountForImport = (value) => {
  return parseFloat(value.replace(/\s*.%/g, '').replace(',', '.'))
}

/**
 * Cast a string to a boolean
 * @param {string} value
 * @returns {boolean}
 */
export const castToBoolean = (value) => {
  const defaultValue = false
  if (typeof value !== 'string') {
    return value === 1 ? true : defaultValue
  } else if (typeof value === 'string') {
    const lowerCaseValue = value.toLowerCase()
    if (lowerCaseValue === 'ja' || lowerCaseValue === 'nein') {
      return lowerCaseValue === 'ja' ? true : defaultValue
    }
  } else {
    return defaultValue
  }
}

/**
 *
 * @param {string|number} value - a (comma-separated) list of numbers or a string
 * @returns {array}
 */
export const castToArrayOfStrings = (value) => {
  if (value === undefined || value === null) {
    return []
  }
  if (typeof value !== 'string') {
    value = `${value}`
  }
  return Array.from(value
    .replace(/\s/g, '')
    .split(',')
    .filter(item => item.length > 0))
}

/**
 * Manipulates columnData and add column definitions as needed
 * @param {array} columnData
 * @returns {array} columnData
 */
export const updateColumns = (columnData) => {
  let hasDiscountField = false
  let hasFirstNameField = false
  let hasLastNameField = false
  let hasCompanyField = false
  let hasKindField = false
  let hasCustomerNumberField = false
  let hasNameField = false

  // iterate over columns and add as needed
  for (let i = 0; i < columnData.length; i++) {
    hasFirstNameField = columnData[i].column.toLowerCase() === 'vorname'
    hasLastNameField = columnData[i].column.toLowerCase() === 'nachname'
    hasCompanyField = columnData[i].column.toLowerCase() === 'firmenname'
    hasDiscountField = columnData[i].column.toLowerCase() === 'rabatt'
    hasKindField = columnData[i].column.toLowerCase() === 'art'
    hasCustomerNumberField = columnData[i].column.toLowerCase() === 'kunden-nr.'
    hasNameField = columnData[i].column.toLowerCase() === 'name'
  }

  if (!hasFirstNameField) {
    columnData.push({
      column: 'vorname',
      alias: 'vorname',
    })
  }

  if (!hasFirstNameField) {
    columnData.push({
      column: 'vorname',
      alias: 'vorname',
    })
  }

  if (!hasLastNameField) {
    columnData.push({
      column: 'nachname',
      alias: 'nachname',
    })
  }

  if (!hasCompanyField) {
    columnData.push({
      column: 'firmenname',
      alias: 'firmenname',
    })
  }

  if (!hasDiscountField) {
    columnData.push({
      column: 'rabatt',
      alias: 'rabatt',
    })
  }

  if (!hasKindField) {
    columnData.push({
      column: 'art',
      alias: 'art',
    })
  }

  if (!hasCustomerNumberField) {
    columnData.push({
      column: 'kunden-nr.',
      alias: 'kunden-nr.',
    })
  }

  if (!hasNameField) {
    columnData.push({
      column: 'name',
      alias: 'name',
    })
  }

  return columnData
}
